import React, { useState } from 'react';
import { Button, Popconfirm, Tag, Tooltip, Dropdown, Menu } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faCircleCheck,
  faGear,
  faPenToSquare,
  faPause,
  faShareNodes,
  faRotate,
  faTrophy,
  faTrash,
  faFile,
  faFileCircleCheck,
  faEllipsisVertical,
  faLocationDot,
  faBriefcase,
  faUsers,
  faGlobe,
  faChair,
  faClipboard,
} from '@fortawesome/free-solid-svg-icons';
import ResponsibleModal from '../ResponsibleModal/ResponsibleModal';
import moment from 'moment';
import { openNotificationWithIcon } from '@utilities/notificationWithIcon';
import './CardInfo.scss';
import ClosingProcessReasonModal from '../closing_process_reason_modal/ClosingProcessReasonModal';
import ClosingReasonInfo from '../closing_reason_info/ClosingReasonInfo';
import ClosingReasonForm from '../closing_reason_form/ClosingReasonForm';
import { SelectionProcessesService } from '@services/selection_processes_service';
import RequestTypeSelectorModal from '../RequestTypeSelectorModal/RequestTypeSelectorModal';
import { getTimeFrom } from '@frontend/helpers/formatDate';
import { DEFAULT_REQUISITION_ACTIVE_MODULES } from '@frontend/src/core/workflows/hiring_workflow_app_modules';

const CardInfo = ({
  selectionProcess,
  onPauseProcess,
  onFinishProcess,
  onActivateProcess,
  onRequestClick,
  onResponsibleClick,
  onDeleteClick,
  onUpdateAsTemplate,
  isBasicWithUserColumns,
  closingReasons,
  canSeeMenu,
  currentUser,
  userRole,
}) => {
  const [openResponsibleModal, setOpenResponsibleModal] = useState(false);
  const [showCloseReasonModal, setShowReasonModal] = useState(false);
  const [showInfoClosingReasonModal, setOpenInfoClosingReason] = useState(false);
  const [openRequestType, setOpenRequestType] = useState(false);

  const selectionProcessesService = new SelectionProcessesService();

  const currentUserIsAdmin = currentUser.is_admin || userRole === 'admin';
  const currentUserIsCollaborator = userRole === 'user';

  // Show 8 stages total: 6 regular + 2 highlighted, rest goes to +N
  const MAX_VISIBLE_STAGES = 8;

  const lists = selectionProcess.lists || [];
  const totalStages = lists.length;
  const visibleLists = lists.slice(0, MAX_VISIBLE_STAGES);
  const hiddenLists = lists.slice(MAX_VISIBLE_STAGES);
  const hasMoreStages = hiddenLists.length > 0;

  const hasSyncIntegration =
    selectionProcess['has_laborum?'] ||
    selectionProcess['has_zonajobs?'] ||
    selectionProcess['has_bumeran?'] ||
    selectionProcess['has_trabajando?'] ||
    selectionProcess['has_chiletrabajos?'] ||
    selectionProcess['has_bne?'] ||
    selectionProcess['has_reqlut?'];

  const getStatusIndicator = (status) => {
    const statusConfig = {
      active: { color: '#52c41a', label: 'Proceso Activo' },
      suspended: { color: '#8c8c8c', label: 'Proceso Suspendido' },
      draft: { color: '#8c8c8c', label: 'Proceso en Borrador' },
      closed: { color: '#1890ff', label: 'Proceso Cerrado' },
    };

    const config = statusConfig[status] || statusConfig.closed;

    return (
      <Tooltip title={config.label} placement="bottom">
        <span className="status-dot" style={{ backgroundColor: config.color }} />
      </Tooltip>
    );
  };

  const handleTrabajandoSync = (e) => {
    e.preventDefault();
    openNotificationWithIcon(
      'warning',
      'Sincronización activada',
      'Se está realizando la sincronización de candidatos. No vuelvas a presionar el botón mientras se ejecuta esta acción',
      4
    );

    setTimeout(
      () =>
        (window.location.href = `/selection_processes/${selectionProcess.id}/update_selection_process_applicant_list`),
      2000
    );
  };

  const handleClickInvalidProcess = () => {
    openNotificationWithIcon(
      'warning',
      'Configuración de proceso',
      <>
        Este proceso aún no tiene configurados los campos básicos para acceder
        al tablero (título y descripción). Realiza estas configuraciones en la
        edición del proceso.
        <Button
          type="link"
          href={`/selection_processes/${selectionProcess.id}/edit`}
        >
          Editar
        </Button>
      </>
    );
  };

  const handleRequestType = (response) => {
    if (response && response.id !== selectionProcess.request_type_data?.id) {
      selectionProcessesService.updateRequestType(selectionProcess.id, {
        request_type_id: response.id,
        request_type: response.name,
      });
      selectionProcess.request_type_data.id = response.id;
      selectionProcess.request_type_data.name = response.name;
      setOpenRequestType(false);
    }
  };

  const getPublicationsCount = () => {
    const publications = selectionProcess.publications || [];
    return publications.length;
  };

  const getTotalCandidates = () => {
    if (selectionProcess.total_applicants !== undefined) {
      return selectionProcess.total_applicants;
    }
    return lists.reduce((acc, list) => acc + (list.active_applicants || 0), 0);
  };

  const getLastActivityText = () => {
    const postulationDate = selectionProcess.postulation_date || selectionProcess.created_at;
    if (postulationDate) {
      return getTimeFrom(moment(postulationDate).locale('es'));
    }
    return null;
  };

  const moreActionsMenu = (
    <Menu>
      {selectionProcess.status?.name === 'active' && (
        <Menu.Item key="pause">
          <Popconfirm
            title="¿Estás seguro de suspender el proceso?"
            onConfirm={() => onPauseProcess(selectionProcess.id)}
            okText="Aceptar"
            cancelText="Cancelar"
          >
            <span>
              <FontAwesomeIcon icon={faPause} style={{ marginRight: 8 }} />
              Suspender proceso
            </span>
          </Popconfirm>
        </Menu.Item>
      )}

      {((selectionProcess.status?.name !== 'active' && currentUserIsAdmin) ||
        ((selectionProcess.status?.name === 'draft' ||
          selectionProcess.status?.name === 'suspended') &&
          currentUserIsCollaborator)) && (
        <Menu.Item key="activate">
          <Popconfirm
            title={`¿Estás seguro que deseas ${
              selectionProcess.status?.name === 'suspended' ? 'reactivar' : 'activar'
            } este proceso?`}
            onConfirm={() => onActivateProcess(selectionProcess.id)}
            okText="Activar"
            cancelText="Cancelar"
          >
            <span>
              <FontAwesomeIcon icon={faPlay} style={{ marginRight: 8 }} />
              Activar proceso
            </span>
          </Popconfirm>
        </Menu.Item>
      )}

      {selectionProcess.status?.name !== 'closed' && (
        <Menu.Item key="close">
          <Popconfirm
            title="¿Estás seguro de cerrar el proceso?"
            onConfirm={() => setShowReasonModal(true)}
            okText="Aceptar"
            cancelText="Cancelar"
          >
            <span>
              <FontAwesomeIcon icon={faTrophy} style={{ marginRight: 8 }} />
              Cerrar proceso
            </span>
          </Popconfirm>
        </Menu.Item>
      )}

      {currentUserIsAdmin && (
        <>
          <Menu.Divider />
          <Menu.Item key="template">
            <Popconfirm
              title={
                selectionProcess.is_template
                  ? '¿Deseas remover este proceso de las plantillas?'
                  : '¿Deseas marcar este proceso como plantilla?'
              }
              onConfirm={() =>
                onUpdateAsTemplate(selectionProcess.id, !selectionProcess.is_template)
              }
              okText={selectionProcess.is_template ? 'Remover' : 'Marcar'}
              cancelText="Cancelar"
            >
              <span>
                <FontAwesomeIcon
                  icon={selectionProcess.is_template ? faFileCircleCheck : faFile}
                  style={{ marginRight: 8 }}
                />
                {selectionProcess.is_template
                  ? 'Remover como plantilla'
                  : 'Marcar como plantilla'}
              </span>
            </Popconfirm>
          </Menu.Item>

          <Menu.Item key="delete" danger>
            <Popconfirm
              title="¿Estás seguro de eliminar el proceso? (El proceso será eliminado de forma permanente)"
              onConfirm={() => onDeleteClick(selectionProcess.id)}
              okText="Eliminar"
              cancelText="Cancelar"
            >
              <span>
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: 8 }} />
                Eliminar proceso
              </span>
            </Popconfirm>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const renderStage = (list, index) => {
    const isValid = selectionProcess.title && selectionProcess.description;

    return (
      <Tooltip key={index} title={list.name} placement="top">
        <div className="pipeline-stage">
          <div className="stage-count">
            {String(list.active_applicants || 0).padStart(2, '0')}
          </div>
          <div className="stage-name">
            {isValid ? (
              <a href={`/selection_processes/${selectionProcess.id}`}>
                {list.name}
              </a>
            ) : (
              <span onClick={handleClickInvalidProcess} className="invalid-link">
                {list.name}
              </span>
            )}
          </div>
        </div>
      </Tooltip>
    );
  };

  const renderPipeline = () => {
    if (totalStages === 0) {
      return <div className="no-stages">Sin etapas configuradas</div>;
    }

    return (
      <>
        {visibleLists.map((list, index) => renderStage(list, index))}
        {hasMoreStages && (
          <Tooltip
            title={hiddenLists.map((l) => `${l.name}: ${l.active_applicants || 0}`).join(', ')}
          >
            <div className="more-stages">+{hiddenLists.length}</div>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <div className="new-card-info">
      {/* Header */}
      <div className="card-header-row">
        <div className="header-left">
          {getStatusIndicator(selectionProcess.status?.name)}
          <Tooltip title={selectionProcess.title} placement="top">
            {selectionProcess.title && selectionProcess.description ? (
              <a
                href={`/selection_processes/${selectionProcess.id}`}
                className="process-title"
              >
                {selectionProcess.title}
              </a>
            ) : (
              <span className="process-title invalid" onClick={handleClickInvalidProcess}>
                {selectionProcess.title || 'Sin título'}
              </span>
            )}
          </Tooltip>

          {selectionProcess.is_template && (
            <Tag className="template-tag">Plantilla</Tag>
          )}
        </div>

        <div className="header-actions">
          {(canSeeMenu || !isBasicWithUserColumns) && (
            <>
              {/* Edit Process Button */}
              <Tooltip
                title={
                  selectionProcess.status?.name === 'closed'
                    ? 'Ver proceso'
                    : 'Editar proceso'
                }
                placement="bottom"
              >
                <Button
                  type="text"
                  size="small"
                  href={`/selection_processes/${selectionProcess.id}/edit`}
                >
                  <FontAwesomeIcon
                    icon={
                      selectionProcess.status?.name === 'closed'
                        ? faGear
                        : faPenToSquare
                    }
                  />
                </Button>
              </Tooltip>

              {/* Share Button */}
              {selectionProcess.status?.name === 'active' && (
                <Tooltip title="Compartir proceso" placement="bottom">
                  <Button
                    type="text"
                    size="small"
                    href={`/selection_processes/${selectionProcess.id}/publish`}
                  >
                    <FontAwesomeIcon icon={faShareNodes} />
                  </Button>
                </Tooltip>
              )}

              {/* View Request Button */}
              {selectionProcess.workflow_id && (
                <Tooltip title="Ver solicitud" placement="bottom">
                  <Button
                    type="text"
                    size="small"
                    onClick={() => onRequestClick(selectionProcess.workflow_id)}
                  >
                    <FontAwesomeIcon icon={faClipboard} />
                  </Button>
                </Tooltip>
              )}

              {/* More Actions Menu */}
              <Dropdown overlay={moreActionsMenu} trigger={['click']} placement="bottomRight">
                <Button type="text" size="small">
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </Button>
              </Dropdown>
            </>
          )}
        </div>
      </div>

      {/* Info Tags Row */}
      <div className="info-tags-row">
        <Tag className="info-tag">ID {selectionProcess.id}</Tag>

        {selectionProcess.workflow_id && (
          <Tag className="info-tag">
            REQ {String(selectionProcess.workflow_id).padStart(5, '0')}
          </Tag>
        )}

        {(selectionProcess.enterprise_area || selectionProcess.area) && (
          <Tag className="info-tag">
            <FontAwesomeIcon icon={faBriefcase} />
            <span>{selectionProcess.enterprise_area?.name || selectionProcess.area}</span>
          </Tag>
        )}

        {selectionProcess.location && (
          <Tag className="info-tag">
            <FontAwesomeIcon icon={faLocationDot} />
            <span>{selectionProcess.location}</span>
          </Tag>
        )}

        {selectionProcess.vacancies && (
          <Tag className="info-tag">
            <FontAwesomeIcon icon={faChair} />
            <span>{selectionProcess.vacancies} vacante(s)</span>
          </Tag>
        )}

        <Tag className="info-tag">
          <FontAwesomeIcon icon={faUsers} />
          <span>{getTotalCandidates()} candidatos</span>
        </Tag>
      </div>

      {/* Pipeline Stages */}
      <div className="pipeline-container">
        {renderPipeline()}
      </div>

      {/* Footer */}
      <div className="card-footer-row">
        <div className="footer-left">
          {hasSyncIntegration && (
            <Tooltip title="Sincronizar candidatos" placement="top">
              <Button
                type="text"
                size="small"
                className="sync-button"
                onClick={handleTrabajandoSync}
              >
                <FontAwesomeIcon icon={faRotate} />
              </Button>
            </Tooltip>
          )}
          <div className="publication-info">
            <FontAwesomeIcon icon={faGlobe} />
            <span>
              Publicado en el portal interno de la empresa
              {getPublicationsCount() > 0 && (
                <>
                  {' y en '}
                  <strong>{getPublicationsCount()} portales de empleo</strong>
                </>
              )}
            </span>
          </div>
        </div>

        <div className="timestamps">
          <span className="timestamp">
            Creado {getTimeFrom(moment(selectionProcess.created_at).locale('es'))}
          </span>
          {getLastActivityText() && (
            <span className="timestamp">
              Última actividad {getLastActivityText()}
            </span>
          )}
        </div>
      </div>

      {/* Modals */}
      {openResponsibleModal && (
        <ResponsibleModal
          selectionProcess={selectionProcess}
          handleOpen={setOpenResponsibleModal}
          isOpen={openResponsibleModal}
          onSubmitResponsible={(e, form) => {
            onResponsibleClick(e, form);
            setOpenResponsibleModal(false);
          }}
        />
      )}

      {showCloseReasonModal && (
        <ClosingProcessReasonModal
          onCloseModal={() => setShowReasonModal(false)}
          showModal={showCloseReasonModal}
        >
          <ClosingReasonForm
            onSubmit={(e, form) => onFinishProcess(e, form, selectionProcess.id)}
            closingReasons={closingReasons}
          />
        </ClosingProcessReasonModal>
      )}

      {showInfoClosingReasonModal && (
        <ClosingProcessReasonModal
          onCloseModal={() => setOpenInfoClosingReason(false)}
          showModal={showInfoClosingReasonModal}
        >
          <ClosingReasonInfo
            closingReason={selectionProcess.closing_reason}
            closingReasonComment={selectionProcess.closing_reason_comment}
          />
        </ClosingProcessReasonModal>
      )}

      {openRequestType && (
        <RequestTypeSelectorModal
          id={selectionProcess.request_type_data?.id}
          activeModules={DEFAULT_REQUISITION_ACTIVE_MODULES}
          onChange={handleRequestType}
          onCancel={() => setOpenRequestType(false)}
          visible={openRequestType}
        />
      )}
    </div>
  );
};

export default CardInfo;
